import { useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Badge, Banner, Button, ConfirmDialog, Page, Spinner, Table, type Column } from "../../components";
import { statePillTone } from "../stageRoute";
import { tDynamic } from "../../i18n/t";
import { usePractice } from "../../api/hooks";
import {
  useBatchRun,
  useBatchRunClients,
  useBatchRunCompleteness,
  useCancelBatchRun,
  useSendBatchRunApprovals,
  useStartBatchRun,
} from "../../api/hooks";
import { formatDateTime, type ClientCompleteness, type ClientMigrationState } from "../../api/types";
import { t } from "../../i18n/t";

const TERMINAL_BATCH_STATUSES = new Set(["CANCELLED", "COMPLETED"]);

/** `/practice/:practiceId/batch-runs/:batchRunId` (P5-web §2e). */
export function BatchRunDetailScreen(): JSX.Element {
  const { batchRunId } = useParams();
  const id = batchRunId ?? "";
  const detail = useBatchRun(id === "" ? null : id);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const clients = useBatchRunClients(id, cursor);
  const completeness = useBatchRunCompleteness(id);
  const sendApprovals = useSendBatchRunApprovals(id);
  const startRun = useStartBatchRun(id);
  const cancelRun = useCancelBatchRun(id);
  // The breadcrumb names the practice; called before the early returns like every other hook.
  const practice = usePractice(detail.data?.practice_id ?? null);

  const [notice, setNotice] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  if (detail.isPending) {
    return (
      <Page title={t("practice.batch_run_detail.title", { id })}>
        <Spinner />
      </Page>
    );
  }
  if (detail.isError || detail.data === undefined) {
    return (
      <Page title={t("practice.batch_run_detail.title", { id })}>
        <Banner tone="block">{t("practice.batch_run_detail.not_found")}</Banner>
      </Page>
    );
  }

  const run = detail.data;
  const isTerminal = TERMINAL_BATCH_STATUSES.has(run.status.toUpperCase());
  const clientIds = (clients.data?.items ?? []).map((row) => row.client_id);

  async function onSendApprovals(): Promise<void> {
    await sendApprovals.mutateAsync({ client_ids: clientIds, scopes: [] });
    // No confirmation banner: the standing note below the meta card already says delivery is pending.
  }

  async function onStart(): Promise<void> {
    await startRun.mutateAsync();
    setNotice(t("practice.batch_run_detail.start_notice"));
  }

  async function onCancel(): Promise<void> {
    await cancelRun.mutateAsync();
    setConfirmCancel(false);
    setNotice(t("practice.batch_run_detail.cancelled_notice"));
  }

  const practiceName = practice.data?.name ?? "";

  const clientCount = (clients.data?.items ?? []).length;
  const buckets = countBuckets(run.counts);

  return (
    <Page
      breadcrumb={
        <nav className="breadcrumb" aria-label={t("a11y.breadcrumb")}>
          <Link to="/practice">{t("practice.landing.title")}</Link> <span aria-hidden="true">/</span>{" "}
          <Link to={`/practice/${run.practice_id}`}>
            {practiceName === "" ? t("practice.detail.loading_title") : practiceName}
          </Link>{" "}
          <span aria-hidden="true">/</span> {t("practice.detail.tab_batch_runs")}
        </nav>
      }
      title={`${t("practice.batch_run_detail.title")} ${run.batch_run_id}`}
      titleAccessory={
        <Badge tone={statePillTone("connecting")} dot>
          {run.status}
        </Badge>
      }
      actions={
        <>
          <Button
            size="compact"
            onClick={() => void onSendApprovals()}
            disabled={sendApprovals.isPending || isTerminal}
          >
            {t("practice.batch_run_detail.send_approvals")}
          </Button>
          <Button
            size="compact"
            variant="primary"
            onClick={() => void onStart()}
            disabled={startRun.isPending || isTerminal}
          >
            {t("practice.batch_run_detail.start")}
          </Button>
          <Button
            size="compact"
            variant="destructive"
            onClick={() => setConfirmCancel(true)}
            disabled={cancelRun.isPending || isTerminal}
          >
            {t("practice.batch_run_detail.cancel")}
          </Button>
        </>
      }
    >
      <section className="card" aria-label={t("practice.batch_run_detail.title")}>
        <dl className="meta3">
          <div>
            <dt className="eyebrow">{t("practice.batch_run_detail.kind")}</dt>
            <dd>{run.kind}</dd>
          </div>
          <div>
            <dt className="eyebrow">{t("practice.batch_run_detail.created_at")}</dt>
            <dd className="mono">{formatDateTime(run.created_at)}</dd>
          </div>
          <div>
            <dt className="eyebrow">{t("practice.batch_run_detail.next_action")}</dt>
            <dd>{run.next_action ?? t("common.none")}</dd>
          </div>
        </dl>
      </section>

      {/* Approval delivery is pending backend implementation — said inline (BatchRunDetail.html), and the
          action notices below it are the outcomes of actually clicking. */}
      <Banner tone="warn">{t("practice.batch_run_detail.approvals_notice")}</Banner>
      {notice === null ? null : <Banner tone="info">{notice}</Banner>}

      <section className="card" aria-labelledby="counts-title">
        <div className="card__header card__header--row">
          <h2 className="card__title" id="counts-title">
            {t("practice.batch_run_detail.counts_title")}
          </h2>
          <span className="pagemeta">
            {t("practice.batch_run_detail.clients_label", { count: clientCount })}
          </span>
        </div>
        <div
          role="img"
          aria-label={t("practice.batch_run_detail.counts_caption") + ": " + bucketsLabel(buckets)}
          className="countbar"
        >
          {buckets.map(([bucket, n]) =>
            n === 0 ? null : (
              <span key={bucket} className={`countbar__seg--${bucket}`} style={{ flex: `${n} 1 0%` }} />
            ),
          )}
        </div>
        <div className="countlegend">
          {buckets.map(([bucket, n]) => (
            <span className="lg" key={bucket}>
              <i className={`countbar__seg--${bucket}`} />
              {legendLabel(bucket)} <span className="mono">{n}</span>
            </span>
          ))}
        </div>
      </section>

      <section className="card card--flush" aria-labelledby="batch-clients-title">
        <div className="card__header">
          <h2 className="card__title" id="batch-clients-title">
            {t("practice.batch_run_detail.clients_title")}
          </h2>
        </div>
        {clients.isPending || completeness.isPending ? (
          <Spinner />
        ) : (
          <Table
            bare
            caption={t("practice.batch_run_detail.clients_caption")}
            columns={mergedClientColumns}
            rows={mergedClients(clients.data?.items ?? [], completeness.data?.clients ?? [])}
            rowKey={(row) => row.client_id}
          />
        )}
        <div className="table-pager">
          <Button
            size="compact"
            disabled={cursors.length === 1}
            onClick={() => setCursors((prev) => prev.slice(0, -1))}
          >
            {t("practice.clients.prev_page")}
          </Button>
          <Button
            size="compact"
            disabled={(clients.data?.next_cursor ?? null) === null}
            onClick={() => setCursors((prev) => [...prev, clients.data?.next_cursor ?? null])}
          >
            {t("practice.clients.next_page")}
          </Button>
        </div>
      </section>

      <ConfirmDialog
        open={confirmCancel}
        title={t("practice.batch_run_detail.cancel_confirm_title")}
        destructive
        onConfirm={() => void onCancel()}
        onCancel={() => setConfirmCancel(false)}
      >
        <p>{t("practice.batch_run_detail.cancel_confirm_body")}</p>
      </ConfirmDialog>
    </Page>
  );
}

/** The counts bar's five buckets (BatchRunDetail.html): hypercare, running, awaiting sign-off, queued, failed. */
type Bucket = "pass" | "run" | "warn" | "queued" | "fail";

function countBuckets(counts: Record<string, number>): readonly [Bucket, number][] {
  const totals: Record<Bucket, number> = { pass: 0, run: 0, warn: 0, queued: 0, fail: 0 };
  for (const [state, n] of Object.entries(counts)) {
    const tone = statePillTone(state as never);
    const bucket: Bucket =
      state === "hypercare"
        ? "pass"
        : tone === "run"
          ? "run"
          : tone === "warn"
            ? "warn"
            : tone === "fail"
              ? "fail"
              : "queued";
    totals[bucket] += n;
  }
  return [
    ["pass", totals.pass],
    ["run", totals.run],
    ["warn", totals.warn],
    ["queued", totals.queued],
    ["fail", totals.fail],
  ];
}

function legendLabel(bucket: Bucket): string {
  if (bucket === "pass") return t("practice.batch_run_detail.legend_hypercare");
  if (bucket === "run") return t("practice.batch_run_detail.legend_running");
  if (bucket === "warn") return t("practice.batch_run_detail.legend_awaiting");
  if (bucket === "queued") return t("practice.batch_run_detail.legend_queued");
  return t("practice.batch_run_detail.legend_failed");
}

function bucketsLabel(buckets: readonly [Bucket, number][]): string {
  return buckets
    .filter(([, n]) => n > 0)
    .map(([bucket, n]) => `${n} ${legendLabel(bucket)}`)
    .join(", ");
}

type MergedClient = ClientCompleteness & { next_action?: string | null };

function mergedClients(
  clients: readonly ClientMigrationState[],
  completeness: readonly ClientCompleteness[],
): readonly MergedClient[] {
  const actionsByClient = new Map(clients.map((row) => [row.client_id, row.next_action ?? null]));
  return completeness.map((row) => ({ ...row, next_action: actionsByClient.get(row.client_id) ?? null }));
}

const mergedClientColumns: readonly Column<MergedClient>[] = [
  {
    key: "name",
    header: t("practice.batch_run_detail.col_client"),
    render: (row) => <span className="cellvendor__name">{row.display_name}</span>,
  },
  {
    key: "state",
    header: t("practice.batch_run_detail.col_migration_state"),
    render: (row) => (
      <Badge tone={statePillTone(row.state as never)} dot>
        {tDynamic("state.", row.state)}
      </Badge>
    ),
  },
  {
    key: "approval",
    header: t("practice.batch_run_detail.col_approval_outcome"),
    render: (row) => row.approval_outcome ?? t("common.none"),
  },
  {
    key: "control_totals",
    header: t("practice.batch_run_detail.col_control_totals"),
    render: (row) =>
      row.control_totals_tie === null || row.control_totals_tie === undefined ? (
        <span className="muted">—</span>
      ) : row.control_totals_tie ? (
        <span className="checkline--pass">{t("common.yes")}</span>
      ) : (
        <span className="numfail">{t("common.no")}</span>
      ),
  },
  {
    key: "next_action",
    header: t("practice.batch_run_detail.col_next_action"),
    render: (row) => <span className="muted">{row.next_action ?? "—"}</span>,
  },
];
